<template>
  <div class="ces-user-role-config">
    <div class="rj-page-card">
      <div class="rj-page-card__title">
        CES角色审批人配置
      </div>
      <div class="rj-page-card__content">
        <p class="page-tip">
          固定审批角色在此绑人（仅影响新单）。商务经理（BIZ_MANAGER）审批人来自 business_leader_relation 专员↔大区经理对照，可在本页维护。汇率告警（EXCHANGE_RATE_ALERT）用于无公司汇率技术告警收件人，非审批节点。
        </p>

        <div class="panel-layout">
          <div class="role-panel">
            <div class="panel-head">
              <span class="panel-title">角色列表</span>
              <div class="panel-actions">
                <el-select
                  v-model="tagFilter"
                  placeholder="分类"
                  class="tag-select"
                  @change="loadRoles"
                >
                  <el-option label="国际退款" value="overseas_refund" />
                  <el-option label="全部启用" value="" />
                </el-select>
                <el-button size="mini" :loading="roleLoading" @click="loadRoles">
                  刷新
                </el-button>
              </div>
            </div>
            <el-table
              v-loading="roleLoading"
              :data="roles"
              border
              highlight-current-row
              height="500"
              class="role-table"
              @current-change="onRoleChange"
            >
              <el-table-column prop="roleName" label="角色名称" min-width="110" show-overflow-tooltip />
              <el-table-column prop="roleCode" label="角色编码" min-width="130" show-overflow-tooltip />
            </el-table>
          </div>

          <div class="binding-panel">
            <div class="panel-head">
              <span class="panel-title">
                {{ isLeaderRelation ? '专员↔大区经理对照' : '已绑人员' }}
                <span v-if="currentRoleCode" class="current-role">· {{ currentRoleLabel }}</span>
                <span v-else class="current-role muted">· 请先选择左侧角色</span>
              </span>
              <el-button
                v-if="isLeaderRelation"
                type="primary"
                size="mini"
                :disabled="!currentRoleCode"
                @click="openLeaderDialog()"
              >
                新增对照
              </el-button>
              <el-button
                v-else
                type="primary"
                size="mini"
                :disabled="!currentRoleCode"
                @click="openBindDialog"
              >
                绑定人员
              </el-button>
            </div>
            <p v-if="isLeaderRelation" class="panel-sub-tip">
              数据来源：business_leader_relation（专员工号/姓名 ↔ 大区经理工号/姓名）。同一专员工号仅允许一条对照，仅影响新单。
            </p>

            <div v-if="isLeaderRelation" class="filter-bar">
              <el-input
                v-model.trim="leaderFilter.commissionerKeyword"
                clearable
                size="small"
                class="filter-input"
                placeholder="区域商务专员姓名/工号/userId"
                @keyup.enter.native="searchLeaderRelations"
              />
              <el-input
                v-model.trim="leaderFilter.managerKeyword"
                clearable
                size="small"
                class="filter-input"
                placeholder="大区经理姓名/工号/userId"
                @keyup.enter.native="searchLeaderRelations"
              />
              <el-button type="primary" size="mini" :disabled="!currentRoleCode" :loading="bindingLoading" @click="searchLeaderRelations">
                查询
              </el-button>
              <el-button size="mini" :disabled="!currentRoleCode" @click="resetLeaderFilter">
                重置
              </el-button>
            </div>

            <!-- BIZ_MANAGER：对照关系表 -->
            <el-table
              v-if="isLeaderRelation"
              v-loading="bindingLoading"
              :data="leaderRelations"
              border
              height="390"
            >
              <el-table-column type="index" label="序号" width="60" :index="indexMethod" />
              <el-table-column prop="commissionerId" label="专员工号" width="110" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.commissionerId || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="commissionerUserId" label="专员userId" width="120" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.commissionerUserId || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="commissionerName" label="专员姓名" min-width="100" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.commissionerName || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="managerId" label="经理工号" width="110" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.managerId || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="managerUserId" label="经理userId" width="120" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.managerUserId || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="managerName" label="经理姓名" min-width="100" show-overflow-tooltip>
                <template v-slot="{ row }">
                  {{ row.managerName || '-' }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="120" fixed="right">
                <template v-slot="{ row }">
                  <el-button type="text" @click="openLeaderDialog(row)">
                    编辑
                  </el-button>
                  <el-button type="text" class="danger-text" @click="deleteLeaderRelationRow(row)">
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <!-- 其它角色：绑人列表 -->
            <el-table
              v-else
              v-loading="bindingLoading"
              :data="bindings"
              border
              height="430"
            >
              <el-table-column type="index" label="序号" width="60" :index="indexMethod" />
              <el-table-column prop="userNum" label="工号" width="120" />
              <el-table-column prop="userName" label="姓名" min-width="100">
                <template v-slot="{ row }">
                  {{ row.userName || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="staffEmail" label="邮箱" min-width="220">
                <template v-slot="{ row }">
                  <span style="font-weight: 600;">{{ row.staffEmail || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" min-width="80">
                <template v-slot="{ row }">
                  {{ row.remark || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="createTime" label="绑定时间" width="170">
                <template v-slot="{ row }">
                  {{ formatTime(row.createTime) }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="90" fixed="right">
                <template v-slot="{ row }">
                  <el-button type="text" class="danger-text" @click="unbind(row)">
                    解绑
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <div class="pager-wrap">
              <el-pagination
                :current-page.sync="page.pageIndex"
                :page-sizes="[10, 20, 50]"
                :page-size.sync="page.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="page.total"
                :disabled="!currentRoleCode"
                @size-change="onPageSizeChange"
                @current-change="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      title="绑定人员"
      :visible.sync="bindDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      @closed="resetBindForm"
    >
      <el-form ref="bindForm" :model="bindForm" :rules="bindRules" label-width="80px">
        <el-form-item label="角色">
          <span>{{ currentRoleLabel }}</span>
        </el-form-item>
        <el-form-item label="用户" prop="userNum">
          <el-select
            v-model="bindForm.userNum"
            class="user-id-select"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="输入姓名模糊匹配"
            :remote-method="remoteUserMethod"
            :loading="userLoading"
            @change="onBindUserChange"
          >
            <el-option
              v-for="item in userOptions"
              :key="item.userID"
              :label="formatUserLabel(item)"
              :value="item.userID"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="bindForm.remark" clearable placeholder="可选" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="bindDialogVisible = false">
          取消
        </el-button>
        <el-button type="primary" :loading="bindingSaving" @click="confirmBind">
          确定
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="leaderForm.id ? '编辑对照' : '新增对照'"
      :visible.sync="leaderDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @closed="resetLeaderForm"
    >
      <el-form ref="leaderForm" :model="leaderForm" :rules="leaderRules" label-width="100px">
        <el-form-item label="专员" prop="commissionerId">
          <el-select
            v-model="leaderForm.commissionerId"
            class="user-id-select"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="输入姓名模糊匹配"
            :remote-method="(q) => remoteLeaderUserMethod(q, 'commissioner')"
            :loading="commissionerLoading"
            @change="onLeaderUserChange('commissioner')"
          >
            <el-option
              v-for="item in commissionerOptions"
              :key="'c-' + item.userID"
              :label="formatUserLabel(item)"
              :value="item.userID"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="大区经理" prop="managerId">
          <el-select
            v-model="leaderForm.managerId"
            class="user-id-select"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="输入姓名模糊匹配"
            :remote-method="(q) => remoteLeaderUserMethod(q, 'manager')"
            :loading="managerLoading"
            @change="onLeaderUserChange('manager')"
          >
            <el-option
              v-for="item in managerOptions"
              :key="'m-' + item.userID"
              :label="formatUserLabel(item)"
              :value="item.userID"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="leaderDialogVisible = false">
          取消
        </el-button>
        <el-button type="primary" :loading="leaderSaving" @click="confirmLeaderSave">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  searchCesUserRoleUser,
  listCesRoles,
  listCesRoleBindings,
  bindCesUserRole,
  unbindCesUserRole,
  saveCesLeaderRelation,
  deleteCesLeaderRelation
} from '@/API';

let userSearchTimer;
let leaderSearchTimer;

export default {
  name: 'CesUserRoleConfig',
  data () {
    return {
      tagFilter: 'overseas_refund',
      roleLoading: false,
      bindingLoading: false,
      bindingSaving: false,
      roles: [],
      bindings: [],
      leaderRelations: [],
      leaderFilter: {
        commissionerKeyword: '',
        managerKeyword: ''
      },
      page: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      },
      currentRoleCode: '',
      currentRoleName: '',
      isLeaderRelation: false,
      bindDialogVisible: false,
      userLoading: false,
      userOptions: [],
      bindForm: {
        userNum: '',
        userName: '',
        staffEmail: '',
        remark: ''
      },
      bindRules: {
        userNum: [{ required: true, message: '请选择用户', trigger: 'change' }]
      },
      leaderDialogVisible: false,
      leaderSaving: false,
      commissionerLoading: false,
      managerLoading: false,
      commissionerOptions: [],
      managerOptions: [],
      leaderForm: {
        id: null,
        commissionerId: '',
        commissionerUserId: '',
        commissionerName: '',
        managerId: '',
        managerUserId: '',
        managerName: ''
      },
      originCommissionerId: '',
      originCommissionerUserId: '',
      originManagerId: '',
      originManagerUserId: '',
      leaderRules: {
        commissionerId: [{ required: true, message: '请选择专员', trigger: 'change' }],
        managerId: [{ required: true, message: '请选择大区经理', trigger: 'change' }]
      }
    };
  },
  computed: {
    currentRoleLabel () {
      if (!this.currentRoleCode) {
        return '请选择左侧角色';
      }
      if (this.currentRoleName) {
        return `${this.currentRoleName}（${this.currentRoleCode}）`;
      }
      return this.currentRoleCode;
    }
  },
  created () {
    this.loadRoles();
  },
  methods: {
    indexMethod (index) {
      return (this.page.pageIndex - 1) * this.page.pageSize + index + 1;
    },
    formatUserLabel (item) {
      if (!item) {
        return '';
      }
      if (item.userName && item.userID) {
        return `${item.userName}（${item.userID}）`;
      }
      return item.userName || item.userID || '';
    },
    formatTime (val) {
      if (!val) {
        return '-';
      }
      if (typeof val === 'string') {
        return val.replace('T', ' ').substring(0, 19);
      }
      try {
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) {
          return String(val);
        }
        const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      } catch (e) {
        return String(val);
      }
    },
    resetPage () {
      this.page.pageIndex = 1;
      this.page.total = 0;
      this.bindings = [];
      this.leaderRelations = [];
    },
    resetLeaderFilter () {
      this.leaderFilter.commissionerKeyword = '';
      this.leaderFilter.managerKeyword = '';
      this.page.pageIndex = 1;
      this.loadBindings();
    },
    searchLeaderRelations () {
      this.page.pageIndex = 1;
      this.loadBindings();
    },
    async loadRoles () {
      this.roleLoading = true;
      try {
        const tag = this.tagFilter === '' || this.tagFilter == null ? undefined : this.tagFilter;
        const { data } = await listCesRoles(tag);
        if (data && data.status === '200') {
          this.roles = data.data || [];
          if (this.currentRoleCode) {
            const still = this.roles.find((r) => r.roleCode === this.currentRoleCode);
            if (!still) {
              this.currentRoleCode = '';
              this.currentRoleName = '';
              this.isLeaderRelation = false;
              this.resetPage();
            } else {
              this.isLeaderRelation = still.roleCode === 'BIZ_MANAGER' ||
                still.bindingSource === 'business_leader_relation';
            }
          }
        }
      } finally {
        this.roleLoading = false;
      }
    },
    onRoleChange (row) {
      if (!row) {
        return;
      }
      this.currentRoleCode = row.roleCode;
      this.currentRoleName = row.roleName || '';
      this.isLeaderRelation = row.roleCode === 'BIZ_MANAGER' ||
        row.bindingSource === 'business_leader_relation';
      this.leaderFilter.commissionerKeyword = '';
      this.leaderFilter.managerKeyword = '';
      this.page.pageIndex = 1;
      this.loadBindings();
    },
    onPageSizeChange () {
      this.page.pageIndex = 1;
      this.loadBindings();
    },
    onPageChange () {
      this.loadBindings();
    },
    async loadBindings () {
      if (!this.currentRoleCode) {
        this.resetPage();
        return;
      }
      this.bindingLoading = true;
      try {
        const params = {
          roleCode: this.currentRoleCode,
          pageIndex: this.page.pageIndex,
          pageSize: this.page.pageSize
        };
        if (this.isLeaderRelation) {
          if (this.leaderFilter.commissionerKeyword) {
            params.commissionerKeyword = this.leaderFilter.commissionerKeyword;
          }
          if (this.leaderFilter.managerKeyword) {
            params.managerKeyword = this.leaderFilter.managerKeyword;
          }
        }
        const { data } = await listCesRoleBindings(params);
        if (data && data.status === '200') {
          const pageData = data.data || {};
          this.isLeaderRelation = pageData.bindingSource === 'business_leader_relation' ||
            this.currentRoleCode === 'BIZ_MANAGER';
          this.page.total = Number(pageData.total || 0);
          if (pageData.pageIndex) {
            this.page.pageIndex = pageData.pageIndex;
          }
          if (pageData.pageSize) {
            this.page.pageSize = pageData.pageSize;
          }
          this.bindings = pageData.bindings || [];
          this.leaderRelations = pageData.leaderRelations || [];
        }
      } finally {
        this.bindingLoading = false;
      }
    },
    openBindDialog () {
      if (!this.currentRoleCode) {
        this.$message.warning('请先选择角色');
        return;
      }
      if (this.isLeaderRelation) {
        this.$message.warning('请使用「新增对照」维护商务经理映射');
        return;
      }
      this.resetBindForm();
      this.bindDialogVisible = true;
    },
    resetBindForm () {
      this.bindForm = {
        userNum: '',
        userName: '',
        staffEmail: '',
        remark: ''
      };
      this.userOptions = [];
      if (this.$refs.bindForm) {
        this.$refs.bindForm.clearValidate();
      }
    },
    remoteUserMethod (query) {
      const keyword = (query || '').trim();
      if (!keyword) {
        this.userOptions = [];
        return;
      }
      if (userSearchTimer) {
        window.clearTimeout(userSearchTimer);
      }
      userSearchTimer = setTimeout(() => {
        this.userLoading = true;
        searchCesUserRoleUser(keyword).then((res) => {
          this.userLoading = false;
          const json = res.data;
          if (json && json.status === '200') {
            this.userOptions = json.data || [];
          } else {
            this.userOptions = [];
          }
        }).catch(() => {
          this.userLoading = false;
          this.userOptions = [];
        });
      }, 200);
    },
    onBindUserChange (val) {
      const selected = this.userOptions.find((item) => item.userID === val);
      this.bindForm.userName = selected ? (selected.userName || '') : '';
      this.bindForm.staffEmail = selected ? (selected.staffEmail || '') : '';
    },
    confirmBind () {
      this.$refs.bindForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        this.bindingSaving = true;
        try {
          const { data } = await bindCesUserRole({
            roleCode: this.currentRoleCode,
            userNum: this.bindForm.userNum,
            userName: this.bindForm.userName,
            staffEmail: this.bindForm.staffEmail,
            remark: this.bindForm.remark
          });
          if (data && data.status === '200') {
            this.$message.success('绑定成功');
            this.bindDialogVisible = false;
            await this.loadBindings();
          }
        } finally {
          this.bindingSaving = false;
        }
      });
    },
    async unbind (row) {
      try {
        await this.$confirm(
          `确认将【${row.userName || row.userNum}】从角色【${this.currentRoleLabel}】解绑？仅影响新单。`,
          '提示',
          { type: 'warning' }
        );
      } catch (e) {
        return;
      }
      const { data } = await unbindCesUserRole({ id: row.id });
      if (data && data.status === '200') {
        this.$message.success('解绑成功');
        await this.loadBindings();
      }
    },
    emptyLeaderForm () {
      return {
        id: null,
        commissionerId: '',
        commissionerUserId: '',
        commissionerName: '',
        managerId: '',
        managerUserId: '',
        managerName: ''
      };
    },
    openLeaderDialog (row) {
      if (!this.currentRoleCode) {
        this.$message.warning('请先选择角色');
        return;
      }
      if (row) {
        this.leaderForm = {
          id: row.id,
          commissionerId: row.commissionerId || '',
          commissionerUserId: row.commissionerUserId || '',
          commissionerName: row.commissionerName || '',
          managerId: row.managerId || '',
          managerUserId: row.managerUserId || '',
          managerName: row.managerName || ''
        };
        this.originCommissionerId = row.commissionerId || '';
        this.originCommissionerUserId = row.commissionerUserId || '';
        this.originManagerId = row.managerId || '';
        this.originManagerUserId = row.managerUserId || '';
        this.commissionerOptions = row.commissionerId
          ? [{ userID: row.commissionerId, userName: row.commissionerName }]
          : [];
        this.managerOptions = row.managerId
          ? [{ userID: row.managerId, userName: row.managerName }]
          : [];
      } else {
        this.leaderForm = this.emptyLeaderForm();
        this.commissionerOptions = [];
        this.managerOptions = [];
        this.originCommissionerId = '';
        this.originCommissionerUserId = '';
        this.originManagerId = '';
        this.originManagerUserId = '';
      }
      this.leaderDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.leaderForm) {
          this.$refs.leaderForm.clearValidate();
        }
      });
    },
    resetLeaderForm () {
      this.leaderForm = this.emptyLeaderForm();
      this.commissionerOptions = [];
      this.managerOptions = [];
      if (this.$refs.leaderForm) {
        this.$refs.leaderForm.clearValidate();
      }
    },
    remoteLeaderUserMethod (query, target) {
      const keyword = (query || '').trim();
      if (!keyword) {
        return;
      }
      if (leaderSearchTimer) {
        window.clearTimeout(leaderSearchTimer);
      }
      leaderSearchTimer = setTimeout(() => {
        if (target === 'commissioner') {
          this.commissionerLoading = true;
        } else {
          this.managerLoading = true;
        }
        searchCesUserRoleUser(keyword).then((res) => {
          const json = res.data;
          const list = (json && json.status === '200' && json.data) ? json.data : [];
          if (target === 'commissioner') {
            this.commissionerOptions = list;
            this.commissionerLoading = false;
          } else {
            this.managerOptions = list;
            this.managerLoading = false;
          }
        }).catch(() => {
          if (target === 'commissioner') {
            this.commissionerLoading = false;
          } else {
            this.managerLoading = false;
          }
        });
      }, 200);
    },
    onLeaderUserChange (target) {
      if (target === 'commissioner') {
        const selected = this.commissionerOptions.find((item) => item.userID === this.leaderForm.commissionerId);
        this.leaderForm.commissionerName = selected ? (selected.userName || '') : this.leaderForm.commissionerName;
        this.leaderForm.commissionerUserId = this.pickLoginUserId(
          selected, this.leaderForm.commissionerId, this.originCommissionerId, this.originCommissionerUserId
        );
        return;
      }
      const selected = this.managerOptions.find((item) => item.userID === this.leaderForm.managerId);
      this.leaderForm.managerName = selected ? (selected.userName || '') : this.leaderForm.managerName;
      this.leaderForm.managerUserId = this.pickLoginUserId(
        selected, this.leaderForm.managerId, this.originManagerId, this.originManagerUserId
      );
    },
    pickLoginUserId (selected, staffNo, originStaffNo, originUserId) {
      if (selected && selected.loginUserId) {
        return selected.loginUserId;
      }
      if (staffNo && staffNo === originStaffNo) {
        return originUserId || '';
      }
      return '';
    },
    confirmLeaderSave () {
      this.$refs.leaderForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        this.leaderSaving = true;
        try {
          const { data } = await saveCesLeaderRelation({ ...this.leaderForm });
          if (data && data.status === '200') {
            this.$message.success(this.leaderForm.id ? '更新成功' : '新增成功');
            this.leaderDialogVisible = false;
            await this.loadBindings();
          }
        } finally {
          this.leaderSaving = false;
        }
      });
    },
    async deleteLeaderRelationRow (row) {
      try {
        await this.$confirm(
          `确认删除【${row.commissionerName || row.commissionerId}】↔【${row.managerName || row.managerId}】对照？仅影响新单。`,
          '提示',
          { type: 'warning' }
        );
      } catch (e) {
        return;
      }
      const { data } = await deleteCesLeaderRelation({ id: row.id });
      if (data && data.status === '200') {
        this.$message.success('删除成功');
        await this.loadBindings();
      }
    }
  }
};
</script>

<style scoped>
.page-tip {
  margin: 0 0 12px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
.panel-sub-tip {
  margin: 0 0 8px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.4;
}
.panel-layout {
  display: flex;
  gap: 16px;
  align-items: stretch;
}
.role-panel {
  width: 360px;
  flex-shrink: 0;
}
.binding-panel {
  flex: 1;
  min-width: 0;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 28px;
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.current-role {
  font-weight: 400;
  color: #606266;
}
.current-role.muted {
  color: #c0c4cc;
}
.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.tag-select {
  width: 120px;
}
.user-id-select {
  width: 100%;
}
.danger-text {
  color: #f56c6c;
}
.pager-wrap {
  margin-top: 10px;
  text-align: right;
}
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.filter-input {
  width: 220px;
}
</style>
<style>
.ces-user-role-config .user-id-select .el-input {
  max-width: none;
  width: 100%;
}
.ces-user-role-config .role-table .el-table__body tr.current-row > td {
  background-color: #ecf5ff !important;
}
</style>
